import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, ViewChild } from "@angular/core";
import { ChartService } from "@services/charts.service";
import { DateService, DateUnit } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { TemperatureProbeCategory } from "@temperature/_enums/temperature/TemperatureProbeCategory.enum";
import { ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureRecordWithDiff } from "@temperature/_models/temperature/ITemperatureRecord";
import './temperature-trend.component.scss';

declare const Highcharts: any;

@Component({
    selector: 'temperature-trend',
    template: require('./temperature-trend.component.html')
})
export class TemperatureTrendComponent implements OnChanges {
    @Input() probe: ITemperatureProbeWithTemperatureData;

    @ViewChild('trendGraph') trendGraph: ElementRef<HTMLElement>;

    // ---


    iconClass: string = '';
    icon: string;
    diff: number;

    latestRecords: ITemperatureRecordWithDiff[];
    latestRecordIsMoreThan24Hours: boolean;

    chartOptions: Highcharts.Options;

    constructor(
        private readonly chartService: ChartService,
        private readonly dateService: DateService,
        private readonly vehicleService: VehicleService,
        private readonly translationService: TranslationService) {
    }


    ngOnChanges(): void {

        this.getProbeLatestRecords();
    }

    isLessThan24Hours(timestamp: Date | string) {
        if (!timestamp) {
            return false;
        }

        const cutOff = this.dateService.addOrSubtractTime(new Date(), -24, DateUnit.Hour);

        if (this.dateService.date1IsAfterDate2(cutOff, timestamp)) {
            return false;
        }

        return true;
    }
    // ---

    private getProbeLatestRecords() {
        this.setProbeName();

        const endTime = new Date(this.probe.latestRecord?.Timestamp);
        const startTime = this.dateService.addOrSubtractTime(endTime, -24, DateUnit.Hour);

        this.latestRecordIsMoreThan24Hours = false;
        if (!this.isLessThan24Hours(endTime)) {
            this.latestRecordIsMoreThan24Hours = true;
            return;
        }

        return this.vehicleService.getVehicleTemperatureRecords(this.probe.VehicleId, startTime, endTime).then((records) => {
            this.probe.latestRecords = records.filter(record => record.TemperatureProbeId === this.probe.Id);
        }).finally(() => {
            this.calculateDiff();
            this.createSparklineChart();
        });
    }

    private createSparklineChart() {
        if (this.chartOptions) {
            return;
        }

        this.chartOptions = this.chartService.createDashboardChartOptions("areaspline");
        this.chartOptions.tooltip.enabled = false;

        const yAxis = this.chartOptions.yAxis as Highcharts.AxisOptions;
        yAxis.labels.enabled = false;
        yAxis.min = -25;
        yAxis.max = 25;

        this.chartOptions.chart.backgroundColor = null;
        this.chartOptions.chart.plotBackgroundColor = null;

        this.chartOptions.series = [{
            type: "spline",
            data: this.createSparklineData() as any
        }];

        const a = this.chartOptions.series[0] as any;
        a.lineWidth = 1;
        a.opacity = 0.5;
        a.zoneAxis = "y";
        a.zones = [
            { color: "#004C93", value: -30 },
            { color: "#004C93", value: 0 },
            { color: "#cf4946", value: 0 },
            { color: "#cf4946", value: 30 }
        ];


        Highcharts.chart(this.trendGraph.nativeElement, this.chartOptions);
    }

    private createSparklineData(): number[][] {
        if (!this.probe?.latestRecords?.length) {
            return [];
        }

        const data: number[][] = [];

        this.probe.latestRecords.sort((x, y) => x.Id - y.Id);

        var lastRecord: ITemperatureRecordWithDiff = null

        this.probe.latestRecords.forEach(record => {
            const t = new Date(record.Timestamp);
            if (lastRecord != null && Math.abs(this.dateService.getDiff(lastRecord.Timestamp, record.Timestamp, DateUnit.Hour)) > 2) {
                data.push([new Date(t).valueOf(), null]);
            }

            data.push([t.valueOf(), record.Temperature]);

            lastRecord = record;
        });

        return data;
    }
    private calculateDiff() {
        if (!this.probe || !this.probe.latestRecord || !this.probe.latestRecords.length) {
            return;
        }

        const cutOff = this.dateService.addOrSubtractTime(this.probe.latestRecord.Timestamp, -30, DateUnit.Min);
        this.probe.latestRecords.sort((x, y) => x.Id - y.Id);

        this.latestRecords = this.probe.latestRecords.filter(rec => {
            return this.dateService.date1IsAfterDate2(rec.Timestamp, cutOff);
        });

        this.createHistoricalDiffData();

        this.diff = this.probe.latestRecord.Temperature - this.latestRecords[0].Temperature;
    }

    private createHistoricalDiffData() {
        let maxDiffCalculation = 0;

        this.probe.latestRecords.forEach((record, i) => {
            if (i == this.probe.latestRecords.length - 1) {
                return;
            }

            const nextRecord = this.probe.latestRecords[i + 1];
            record.diff = record.Temperature - nextRecord.Temperature;
            record.diff = Math.round(record.diff * 10) / 10;

            maxDiffCalculation = Math.max(Math.abs(record.diff), maxDiffCalculation);
        });

        this.probe.latestRecords.forEach(record => {
            if (!record.diff) {
                return;
            }

            record.graphDiff = Math.round(record.diff / maxDiffCalculation * 10);
        });
    }

    private setProbeName() {

        if (this.probe && !this.probe.Name) {
            this.probe.Name = this.translationService.tEnum(TemperatureProbeCategory, "TemperatureProbeCategory", this.probe.Category);
        }
    }
}
